import { useEffect, useMemo, useState } from 'react'

type TimeLeft = {
  totalMs: number
  hours: number
  minutes: number
  seconds: number
}

type UseCountdownOptions = {
  cutoffHour?: number // Hour of cutoff in UK time (0–23)
  cutoffMinute?: number // Minute of cutoff
  tickMs?: number // How often timer updates (ms)
  forceShowCountdown?: boolean // DEV override flag
}

export type CountdownResult = {
  isBeforeCutoff: boolean
  timeLeft: TimeLeft
}

/**
 * Extracts UK (Europe/London) date parts from a given Date.
 *
 * We cannot rely on the user's device timezone, so we use
 * Intl.DateTimeFormat with timeZone: "Europe/London".
 *
 * This ensures:
 * - Correct GMT/BST handling
 * - Correct DST transitions
 */
const getUkParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const parts = formatter.formatToParts(date)

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  }
}

/**
 * Calculates the timezone offset (in minutes) for UK time at a specific Date.
 *
 * This is required because the UK switches between:
 * - GMT (UTC+0)
 * - BST (UTC+1)
 *
 * We compute the offset dynamically to ensure accuracy across DST changes.
 */
const getUkOffsetMinutes = (date: Date) => {
  const uk = getUkParts(date)

  // Build a UTC timestamp from UK parts
  const asUtc = Date.UTC(
    uk.year,
    uk.month - 1,
    uk.day,
    uk.hour,
    uk.minute,
    uk.second
  )

  // Difference between calculated UTC and actual UTC gives offset
  return Math.round((asUtc - date.getTime()) / 60000)
}

/**
 * Returns the UTC timestamp for a UK cutoff time on a specific UK date.
 *
 * We:
 * 1. Create a naive UTC timestamp
 * 2. Adjust using UK offset at that time
 *
 * This ensures correct behaviour during DST boundary days.
 */
const getUkCutoffTimestamp = (
  ukYear: number,
  ukMonth: number,
  ukDay: number,
  cutoffHour: number,
  cutoffMinute: number
) => {
  const naiveUtc = Date.UTC(
    ukYear,
    ukMonth - 1,
    ukDay,
    cutoffHour,
    cutoffMinute,
    0
  )

  const offset = getUkOffsetMinutes(new Date(naiveUtc))

  // Subtract offset to get true UTC timestamp
  return naiveUtc - offset * 60_000
}

/**
 * Core calculation logic.
 *
 * Determines:
 * - Whether we are before today's cutoff
 * - How much time remains
 *
 * If forceShowCountdown is true (DEV only),
 * and we're past cutoff, we calculate time
 * until tomorrow's cutoff instead.
 */
const calculateTimeLeft = (
  now: Date,
  cutoffHour: number,
  cutoffMinute: number,
  forceShowCountdown: boolean
): CountdownResult => {
  const ukNow = getUkParts(now)

  const todayCutoffTs = getUkCutoffTimestamp(
    ukNow.year,
    ukNow.month,
    ukNow.day,
    cutoffHour,
    cutoffMinute
  )

  let diffMs = todayCutoffTs - now.getTime()
  let isBeforeCutoff = diffMs > 0

  // If we're past cutoff but dev override is enabled
  if (!isBeforeCutoff && forceShowCountdown) {
    // Move forward 24h from today's cutoff
    const tomorrowRef = new Date(todayCutoffTs + 24 * 60 * 60 * 1000)

    const ukTomorrow = getUkParts(tomorrowRef)

    const tomorrowCutoffTs = getUkCutoffTimestamp(
      ukTomorrow.year,
      ukTomorrow.month,
      ukTomorrow.day,
      cutoffHour,
      cutoffMinute
    )

    diffMs = tomorrowCutoffTs - now.getTime()
    isBeforeCutoff = true
  }

  // If still negative, cutoff has passed and no override applied
  if (diffMs <= 0) {
    return {
      isBeforeCutoff: false,
      timeLeft: { totalMs: 0, hours: 0, minutes: 0, seconds: 0 },
    }
  }

  // Convert milliseconds to usable time parts
  const totalSeconds = Math.floor(diffMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return {
    isBeforeCutoff,
    timeLeft: {
      totalMs: diffMs,
      hours,
      minutes,
      seconds,
    },
  }
}

/**
 * Reusable UK cutoff countdown hook.
 *
 * Features:
 * - Works in UK timezone only
 * - Handles GMT/BST automatically
 * - Updates every tickMs
 * - Supports configurable cutoff times
 * - Supports development-only override
 *
 * Designed for ecommerce cut-off messaging.
 */
const useCountdownTimer = (options: UseCountdownOptions = {}) => {
  const {
    cutoffHour = 12,
    cutoffMinute = 0,
    tickMs = 1000,
    forceShowCountdown = false,
  } = options

  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    // Initialise on client to avoid SSR hydration mismatch
    setNow(new Date())

    const interval = window.setInterval(() => {
      setNow(new Date())
    }, tickMs)

    return () => window.clearInterval(interval)
  }, [tickMs])

  const result = useMemo(() => {
    if (!now) return null

    return calculateTimeLeft(now, cutoffHour, cutoffMinute, forceShowCountdown)
  }, [now, cutoffHour, cutoffMinute, forceShowCountdown])

  return result
}

export default useCountdownTimer
